import { create } from "zustand";
import {
	type ChatMessage as ApiMessage,
	type ConversationStatus,
	fetchConversations,
	fetchMessages,
} from "../lib/api";
import type { AgntWebSocket } from "../lib/websocket";

export interface ChatMessage {
	id: string;
	role: "user" | "assistant" | "tool";
	content: string;
	createdAt: string;
	streaming?: boolean;
	/**
	 * P4.5 Slice 4 — speculative streaming retract visual signal.
	 * Bumped to Date.now() inside retractFromLastMessage so MessageBubble's
	 * React.memo sees a new prop value and re-renders. The bubble watches
	 * this via useEffect and toggles a transient CSS class for ~350ms that
	 * plays a brief opacity dip animation — the user sees that something
	 * just got swapped under them instead of an instant text jump.
	 *
	 * Never decreases. Never stamped on the user/tool roles. Cleared on
	 * conversation reload (loadMessages rehydrates from API which doesn't
	 * carry this field).
	 */
	retractedAt?: number;
	/**
	 * P4.7 (2026-05-19) — chat turn's correlation id, stamped by useChat
	 * on the latest assistant message when the WS emits its first
	 * ``correlation_id`` event. Used by InlineProgress to match activity
	 * events fired by tools running inside the same correlation scope.
	 * Cleared on conversation reload.
	 */
	correlationId?: string;
	/**
	 * P4.7 (2026-05-19) — inline progress indicator for long-running
	 * tools (research, compile, news_watch, etc.). When set, MessageBubble
	 * renders an InlineProgress sub-component beneath the bubble showing
	 * phase text + elapsed time. Cleared when the message finalizes
	 * (finalizeLastMessage) OR when the toaster's setMessageProgress
	 * receives a *_completed/*_failed event for this correlation.
	 */
	progress?: {
		phase: string;
		startedAt: number; // epoch ms client-side
	};
	/**
	 * 2026-05-23 — LLM model id that produced this assistant message
	 * (e.g. ``"anthropic/claude-sonnet-4-5"``, ``"ollama/qwen2.5:14b"``).
	 * Stamped by finalizeLastMessage when the server's done chunk
	 * carries ``model``, AND populated from the API on loadMessages.
	 * Empty / undefined → MessageBubble skips rendering the chip
	 * (legacy rows, error paths, providers that didn't surface a
	 * model name). Per-message granularity correctly reflects hybrid
	 * routing where different turns can land on different models.
	 */
	model?: string;
	/**
	 * A1 (2026-09-02) — the bot that authored this assistant reply
	 * (provenance.agent_key). Stamped live by the WS `agent` frame before the
	 * first delta, and rehydrated from the API on load. Absent = main agent.
	 */
	agentKey?: string;
	/** A3 (2026-09-02) — set when a ROUTINE posted this reply: which run and
	 * how it ended. Rendered as a strip above the bubble; rehydrated on load. */
	routine?: { name: string; status: string };
	/**
	 * 2026-07-06 — set when the turn failed (WS `error` frame). Rendered as a
	 * themed error row with a Retry action BELOW the bubble, NOT mixed into
	 * `content` (which previously got a raw "⚠️ …" prefix indistinguishable
	 * from a real reply). Cleared on regenerate. Empty/undefined on success.
	 */
	error?: string;
	/**
	 * 2026-07-07 — image attachments the user sent with this message. Set
	 * optimistically on send (dataUrl = the downscaled preview) and rehydrated
	 * from the API on loadMessages (dataUrl = inline base64 from provenance,
	 * or — for a photo a channel filed into the vault — a same-origin
	 * `/api/attachment/preview/…` URL, 2026-09-07).
	 * Rendered as an image gallery above the user bubble text. User role only.
	 */
	attachments?: ChatAttachment[];
	/**
	 * Message branching (2026-07-07). ``branchCount`` is how many sibling
	 * assistant replies exist for this turn (1 = unbranched); ``branchIndex``
	 * is this active reply's 0-based position among them. MessageActions renders
	 * a ``< n/m >`` switcher only when branchCount > 1. Rehydrated by
	 * loadMessages via mapApiMessages; absent on optimistic/streaming rows
	 * (a reload after the turn finalizes fills them in).
	 */
	branchIndex?: number;
	branchCount?: number;
}

export interface ChatAttachment {
	id: string;
	mimeType: string;
	/**
	 * What the <img> loads: an inline `data:<mime>;base64,…` URI (web-chat
	 * upload) or a same-origin `/api/…` preview URL (channel-filed photo).
	 * Never gate on the `data:` prefix — a URL here is normal.
	 */
	dataUrl: string;
}

export interface ConversationSummary {
	id: string;
	title: string;
	updatedAt: string;
	archived?: boolean;
	pinned?: boolean;
	/** Bot this thread is bound to (A1); absent = the main agent. */
	agentKey?: string;
}

interface ChatState {
	conversations: ConversationSummary[];
	conversationsLoaded: boolean;
	activeConversationId: string | null;
	messages: ChatMessage[];
	/** True while older history exists beyond the loaded window (scroll-to-top loads it). */
	hasMoreOlder: boolean;
	/** created_at of the oldest loaded message — cursor for the next older page. */
	oldestCursor: string | null;
	/** True while an older-history page is in flight (guards the scroll trigger). */
	loadingOlder: boolean;
	isStreaming: boolean;
	activeTool: string | null;
	/**
	 * The server's view of the in-flight turn (polled every ~2.5 s by
	 * useConversationStatus, mirrored here by ChatView). The inline thinking
	 * bubble reads it for the phase, the running tool and the provider's
	 * retry note; MessageList renders a pending bubble from it when this tab
	 * has no streaming bubble of its own (reconnected mid-turn).
	 */
	turnStatus: ConversationStatus | null;
	/**
	 * Message branching (2026-07-07). Set true by editMessage/regenerateLast so
	 * the useChat ``done`` handler reloads the conversation ONCE after the fresh
	 * reply finalizes — that reload is what surfaces the new ``< n/m >`` count
	 * (the streamed reply row has no branch metadata client-side). Cleared on
	 * consume; normal turns never set it, so they don't pay the extra reload.
	 */
	pendingBranchReload: boolean;
	/**
	 * True between "this browser started a turn" and "the server has shown
	 * any sign of having picked it up" (2026-08-05).
	 *
	 * `GET /api/conversations/{id}/status` answers `idle` for BOTH "no turn
	 * ever ran" and "a turn ran and finished" — the reconciler in ChatView
	 * can only read it as the latter, so during the gap before the server
	 * registers our turn it concludes the turn is over and force-clears it.
	 * That closes the late-delta liveness gate below and the real reply is
	 * then dropped chunk by chunk, invisible until a manual reload.
	 *
	 * Defaults FALSE so state adopted from the server (a draft row loaded
	 * after a page reload) is reconciled exactly as before — only a turn
	 * THIS browser started is given the benefit of the doubt, and only
	 * until the server first acknowledges it. The 45s chunk-silence
	 * watchdog in useChat remains the backstop for a turn the server never
	 * acknowledges at all.
	 */
	serverAckPending: boolean;

	setActiveConversation: (id: string | null) => void;
	/** Mark that this browser just started a turn the server has not yet
	 *  acknowledged. Suppresses reconciliation until it does. */
	beginLocalTurn: () => void;
	/** Any evidence the server picked the turn up — a frame on the socket, or
	 *  a polled status that is no longer idle. Idempotent. */
	markTurnAccepted: () => void;
	addMessage: (msg: ChatMessage) => void;
	appendToLastMessage: (delta: string) => void;
	/**
	 * P4.5 — Speculative streaming retract. Remove the trailing N chars
	 * from the last (streaming) message buffer. Counts against TOTAL
	 * emitted chars including the in-flight RAF-buffered tail: trims the
	 * pending tail first, then the on-screen store value, so the caller
	 * doesn't have to know which layer the chars currently live in.
	 * No-op when n <= 0.
	 */
	retractFromLastMessage: (n: number) => void;
	finalizeLastMessage: (model?: string) => void;
	/** Mark the streaming turn as failed: preserve any partial reply, stamp a
	 *  structured `error` (rendered as a retry row, not as prose), clear streaming. */
	setLastMessageError: (error: string) => void;
	setStreaming: (v: boolean) => void;
	setActiveTool: (tool: string | null) => void;
	setTurnStatus: (status: ConversationStatus | null) => void;
	/**
	 * P4.7 (2026-05-19) — stamp the correlation id on the LATEST assistant
	 * message. Called by useChat when a "correlation_id" WS event arrives.
	 * No-op when no streaming assistant message exists. Idempotent — re-
	 * stamping with the same id is a no-op (avoids re-renders).
	 */
	setLastMessageCorrelationId: (correlationId: string) => void;
	/** A1 — stamp the bot that is answering onto the streaming reply. */
	setLastMessageAgent: (agentKey: string) => void;
	/**
	 * P4.7 (2026-05-19) — set or clear the inline progress indicator on
	 * the message matching ``correlationId``. ``phase`` is the human-
	 * readable label ("Searching the web…"); pass null to clear.
	 * ``startedAt`` is the epoch-ms timestamp the elapsed counter ticks
	 * from. No-op when no message matches the correlation id (events
	 * for an already-cleared turn are silently dropped).
	 */
	setMessageProgress: (
		correlationId: string,
		phase: string | null,
		startedAt?: number,
	) => void;
	// Stale-WS watchdog: drop in-flight UI state when the WebSocket goes
	// silent or the server confirms the turn is idle. Flips streaming=false
	// on every message that's still marked streaming so the spinning robot
	// stops, clears isStreaming + activeTool, and (when content==="")
	// replaces the placeholder with a sentinel so the user knows the reply
	// never landed. Returns true if it actually cleared something — caller
	// uses this to log/toast.
	forceClearStream: (reason?: string) => boolean;
	loadConversations: (includeArchived?: boolean) => Promise<void>;
	// Optimistic local removal — drop a conversation from the sidebar instantly
	// on delete; the DB write + poll reconcile in the background (UX must be
	// immediate, never await the server).
	removeConversation: (id: string) => void;
	loadMessages: (conversationId: string) => Promise<void>;
	/** Load one older page (scroll-to-top). Returns the number prepended so the
	 *  caller can preserve scroll position. */
	loadOlderMessages: (conversationId: string) => Promise<number>;
	clearMessages: () => void;
	// Edit/regenerate (Session 5). useChat registers the live WebSocket via
	// setWebSocket on connect and clears it on unmount, so these actions
	// can find the right channel without prop-drilling through every bubble.
	setWebSocket: (ws: AgntWebSocket | null) => void;
	editMessage: (messageId: string, newContent: string) => Promise<void>;
	regenerateLast: () => Promise<void>;
	/**
	 * Message branching (2026-07-07). Switch the active reply of ``messageId``'s
	 * branch group to sibling ``index`` (0-based, oldest-first), then reload the
	 * conversation to show the switched path. No-op while a turn is streaming.
	 */
	switchActiveBranch: (messageId: string, index: number) => Promise<void>;
	/** Clear the pending-branch-reload flag (consumed by the done handler). */
	clearPendingBranchReload: () => void;
}

// WebSocket reference lives outside the React store value so updating it
// never triggers a re-render. Callers go through setWebSocket and the
// actions read it via getWs() inside their closures.
let wsRef: AgntWebSocket | null = null;

let msgCounter = 0;

export function createMessageId(): string {
	return `msg-${Date.now()}-${++msgCounter}`;
}

/** Map an API message page to store ChatMessages. Shared by the initial load
 *  and the older-history pages so both rehydrate model + attachments the same
 *  way. Real rows always carry an id; the prefix only backstops legacy rows. */
function mapApiMessages(items: ApiMessage[], prefix = "loaded"): ChatMessage[] {
	return items.map((m, i) => ({
		id: m.id || `${prefix}-${i}`,
		role: m.role as "user" | "assistant" | "tool",
		content: m.content,
		createdAt: m.created_at || new Date().toISOString(),
		streaming: m.streaming === true,
		...(m.model ? { model: m.model } : {}),
		...(m.agent_key ? { agentKey: m.agent_key } : {}),
		...(m.routine && typeof m.routine === "object" ? { routine: m.routine } : {}),
		...(typeof m.branch_count === "number" && m.branch_count > 1
			? { branchIndex: m.branch_index ?? 0, branchCount: m.branch_count }
			: {}),
		...(m.attachments?.length
			? {
					attachments: m.attachments.map((a) => ({
						id: a.id,
						mimeType: a.mime_type,
						dataUrl: a.data_url,
					})),
				}
			: {}),
	}));
}

// RAF-throttled append: collapse all WebSocket chunks that arrive within one
// animation frame into a single store update. Cuts re-render rate from
// "every chunk" (often 100+/sec on cloud models) to ~60fps. Pairs with
// React.memo on MessageBubble so only the streaming bubble re-renders.
let pendingDelta = "";
let pendingFlushHandle: number | null = null;
// The conversation id the buffered delta belongs to. Streaming is one turn
// at a time, but a user can switch conversations while a RAF flush is still
// queued — without this guard a delta streamed for conversation A would
// append onto conversation B's last message (a real cross-thread text bleed).
let pendingConvId: string | null = null;

/**
 * End the current turn's delta stream: cancel any queued flush and hand back
 * whatever is still buffered so the caller can drain it into the same set()
 * that closes the bubble. EVERY turn-ending path (done / error / force-clear /
 * reload) routes through here — that single-sourcing is load-bearing. It used
 * to be three copy-pasted drain blocks, and a fourth ender that forgot to
 * drain would strand a delta with a live flush queued behind it.
 */
function endTurnStream(): string {
	if (pendingFlushHandle !== null) {
		cancelAnimationFrame(pendingFlushHandle);
		pendingFlushHandle = null;
	}
	const tail = pendingDelta;
	pendingDelta = "";
	pendingConvId = null;
	return tail;
}

// Drop any queued streaming delta and cancel its pending flush. Called on
// every conversation switch / message reload so a buffered chunk can never
// land on the wrong thread.
function resetStreamBuffer(): void {
	endTurnStream();
}

/**
 * Liveness: a delta may only ever land on a bubble that is still streaming.
 * Once a turn is finalized, errored, or force-cleared its bubble is frozen —
 * a late delta has nowhere legitimate to go, so it is dropped rather than
 * concatenated onto finished text.
 *
 * Why (2026-07-15): a bubble could render the force-clear sentinel with a real
 * reply welded onto it — "⚠ The reply did not complete. Please try again.- 32 mm
 * hole drill…". Both forceClearStream callers (the chunk-silence watchdog in
 * useChat, the server-idle reconciler in ChatView) fire on a LIVE socket and
 * clear asynchronously — `loadMessages(...).finally(() => forceClearStream())`
 * — so genuine chunks keep arriving after the sentinel lands. The enders each
 * cancelled the *currently queued* flush, but nothing stopped the NEXT delta
 * from buffering and scheduling a fresh one. The 2026-07-14 turn-detach fix
 * (handler.py) made late frames routine rather than exotic: a turn now survives
 * the client giving up. Locked by chat.lateDelta.test.ts.
 */
/** One dropped-delta warning per turn (reset when a turn begins). */
let lateDeltaWarned = false;

function tailIsStreaming(get: () => ChatState): boolean {
	const msgs = get().messages;
	return msgs[msgs.length - 1]?.streaming === true;
}

function flushAppend(
	set: (fn: (state: ChatState) => Partial<ChatState>) => void,
	get: () => ChatState,
): void {
	pendingFlushHandle = null;
	if (!pendingDelta) return;
	// The user switched conversations before this RAF fired — discard the
	// buffered delta rather than append it onto the now-active thread.
	if (pendingConvId !== null && get().activeConversationId !== pendingConvId) {
		pendingDelta = "";
		pendingConvId = null;
		return;
	}
	const delta = pendingDelta;
	pendingDelta = "";
	set((state) => {
		const msgs = state.messages;
		const last = msgs[msgs.length - 1];
		// Liveness re-checked at paint time — the tail can be replaced by a
		// reload between buffering and this frame.
		if (last?.streaming !== true) return state;
		const next = [...msgs];
		next[msgs.length - 1] = { ...last, content: last.content + delta };
		return { messages: next };
	});
}

export const useChatStore = create<ChatState>((set, get) => ({
	conversations: [],
	conversationsLoaded: false,
	activeConversationId: null,
	messages: [],
	hasMoreOlder: false,
	oldestCursor: null,
	loadingOlder: false,
	isStreaming: false,
	activeTool: null,
	turnStatus: null,
	pendingBranchReload: false,
	serverAckPending: false,

	setActiveConversation: (id) => {
		// Any streaming delta buffered for the previous conversation is now
		// stale — drop it so it can't flush onto the newly-active thread.
		resetStreamBuffer();
		// A pending ack belongs to the thread we just left.
		set({ activeConversationId: id, serverAckPending: false });
	},

	beginLocalTurn: () => {
		lateDeltaWarned = false;
		set({ serverAckPending: true });
	},

	markTurnAccepted: () => {
		if (!get().serverAckPending) return; // avoid a pointless re-render
		set({ serverAckPending: false });
	},

	removeConversation: (id) =>
		set((state) => ({
			conversations: state.conversations.filter((c) => c.id !== id),
		})),

	addMessage: (msg) =>
		set((state) => ({
			messages: [...state.messages, msg],
		})),

	appendToLastMessage: (delta) => {
		// Liveness gate — the bubble this delta would land on may already be
		// finalized, errored, or force-cleared. Buffering here is what welded a
		// real reply onto the "⚠ The reply did not complete." sentinel: the
		// enders only cancelled the queued flush, they could not stop a NEW
		// delta from scheduling a fresh one. Nothing live to receive it → drop.
		if (!tailIsStreaming(get)) {
			// Dropping is right, but dropping SILENTLY is what made the
			// 2026-08-05 incident cost a debugging session: a complete,
			// correctly-persisted reply vanished chunk by chunk with no trace
			// anywhere in the browser. One line per turn — enough to name the
			// symptom, not enough to spam a genuinely late detached turn.
			if (!lateDeltaWarned && typeof console !== "undefined") {
				lateDeltaWarned = true;
				console.warn(
					"[chat] delta arrived with no live bubble to receive it — " +
						"the turn was ended early; reply text is being dropped. " +
						"It is persisted server-side and will appear on reload.",
				);
			}
			return;
		}
		pendingDelta += delta;
		pendingConvId = get().activeConversationId;
		if (pendingFlushHandle !== null) return;
		pendingFlushHandle = requestAnimationFrame(() => flushAppend(set, get));
	},

	retractFromLastMessage: (n) => {
		if (n <= 0) return;
		// Same liveness gate as appendToLastMessage. A late `replace_after`
		// frame carries the same corruption risk in the other direction —
		// silently trimming chars off an already-finished reply.
		if (!tailIsStreaming(get)) return;
		// Cancel any pending RAF flush — we'll handle pendingDelta + store
		// mutations atomically here. If the RAF were to fire after our
		// store-side trim, it would re-append already-retracted text.
		if (pendingFlushHandle !== null) {
			cancelAnimationFrame(pendingFlushHandle);
			pendingFlushHandle = null;
		}
		// Stage 1: trim pendingDelta from the right (in-flight chars the
		// server already counted but the UI hasn't rendered yet).
		let remaining = n;
		if (pendingDelta.length >= remaining) {
			pendingDelta = pendingDelta.slice(0, pendingDelta.length - remaining);
			remaining = 0;
		} else {
			remaining -= pendingDelta.length;
			pendingDelta = "";
		}
		// Stage 2: retract `remaining` chars from the displayed message
		// content (already rendered to the user). Clamp to 0 — if the
		// server's count somehow exceeds what we have, trim everything
		// rather than producing a negative slice.
		//
		// P4.5 Slice 4: ALWAYS stamp retractedAt on the last message so
		// the bubble plays the brief opacity-dip animation. We stamp even
		// when the entire retract was absorbed by pendingDelta (no Stage 2
		// trim needed) because from the user's perspective something
		// visible just happened — chars were about to be rendered and
		// got swallowed. A silent retract here would leak as a perceived
		// stutter ("the agent paused mid-sentence"). The animation tells
		// the user "I just walked something back."
		const retractedAt = Date.now();
		set((state) => {
			const msgs = [...state.messages];
			const last = msgs[msgs.length - 1];
			if (last) {
				const newLen = remaining > 0
					? Math.max(0, last.content.length - remaining)
					: last.content.length;
				msgs[msgs.length - 1] = {
					...last,
					content: remaining > 0 ? last.content.slice(0, newLen) : last.content,
					retractedAt,
				};
			}
			return { messages: msgs };
		});
	},

	finalizeLastMessage: (model?: string) => {
		// Drain any RAF-buffered delta into the same set() that flips
		// streaming=false, so the trailing chunk never gets dropped on done.
		// streaming=false is also what closes the bubble to any LATER delta —
		// it is the flag appendToLastMessage's liveness gate reads.
		const tail = endTurnStream();
		set((state) => {
			const msgs = [...state.messages];
			const last = msgs[msgs.length - 1];
			if (last) {
				msgs[msgs.length - 1] = {
					...last,
					content: last.content + tail,
					streaming: false,
					// P4.7 — clear inline progress on finalize. The final
					// tool result lands via the next chunk; the progress
					// line was for the silent window in between.
					progress: undefined,
					// 2026-05-23 — stamp model when the done frame
					// carried it. Empty string falls through to undefined
					// so the bubble's truthy check skips rendering the
					// chip (preserves legacy when no model surfaced).
					...(model ? { model } : {}),
				};
			}
			// The turn is over — nothing left for the server to acknowledge.
			return { messages: msgs, serverAckPending: false };
		});
	},

	setLastMessageError: (error: string) => {
		// Drain any buffered delta so a partial reply is preserved, stamp the
		// structured error (rendered as a themed retry row, NOT appended into
		// the content as prose), and clear the streaming flag.
		const tail = endTurnStream();
		set((state) => {
			const msgs = [...state.messages];
			const last = msgs[msgs.length - 1];
			if (last) {
				msgs[msgs.length - 1] = {
					...last,
					content: last.content + tail,
					streaming: false,
					progress: undefined,
					error,
				};
			}
			return { messages: msgs, serverAckPending: false };
		});
	},

	setStreaming: (v) => set({ isStreaming: v, activeTool: v ? undefined : null }),
	setActiveTool: (tool) => set({ activeTool: tool }),
	setTurnStatus: (status) => set({ turnStatus: status }),

	// ── P4.7 (2026-05-19) ── correlation id + inline progress ──────────
	setLastMessageCorrelationId: (correlationId) => {
		if (!correlationId) return;
		set((state) => {
			const msgs = state.messages;
			const last = msgs[msgs.length - 1];
			if (!last) return state;
			if (last.role !== "assistant") return state;
			if (last.correlationId === correlationId) return state; // idempotent no-op
			const next = [...msgs];
			next[msgs.length - 1] = { ...last, correlationId };
			return { messages: next };
		});
	},
	setLastMessageAgent: (agentKey) => {
		if (!agentKey) return;
		set((state) => {
			const msgs = state.messages;
			const last = msgs[msgs.length - 1];
			if (!last || last.role !== "assistant") return state;
			if (last.agentKey === agentKey) return state;
			const next = [...msgs];
			next[msgs.length - 1] = { ...last, agentKey };
			return { messages: next };
		});
	},
	setMessageProgress: (correlationId, phase, startedAt) => {
		if (!correlationId) return;
		set((state) => {
			const msgs = state.messages;
			// Find the message with matching correlationId. Typically the
			// last assistant message, but loop in case the user sent a
			// follow-up turn before the prior turn finished.
			let idx = -1;
			for (let i = msgs.length - 1; i >= 0; i--) {
				const m = msgs[i];
				if (m && m.correlationId === correlationId) {
					idx = i;
					break;
				}
			}
			if (idx === -1) return state; // no match — silently drop
			const target = msgs[idx];
			if (!target) return state; // unreachable but typescript-strict
			if (phase === null) {
				// Clear path. No-op when already cleared.
				if (target.progress === undefined) return state;
				const next = [...msgs];
				next[idx] = { ...target, progress: undefined };
				return { messages: next };
			}
			// Idempotent re-stamp avoids re-renders for the same phase
			// (toaster polls every 8s; the same event lands repeatedly).
			if (
				target.progress &&
				target.progress.phase === phase &&
				(startedAt === undefined ||
					target.progress.startedAt === startedAt)
			) {
				return state;
			}
			const next = [...msgs];
			next[idx] = {
				...target,
				progress: {
					phase,
					startedAt: startedAt ?? target.progress?.startedAt ?? Date.now(),
				},
			};
			return { messages: next };
		});
	},

	forceClearStream: (reason) => {
		// Drain any pending RAF append so it can't re-introduce streaming
		// state moments after we clear it. Deltas arriving AFTER this point
		// are stopped by the liveness gate: the map below clears every
		// streaming flag, which is what appendToLastMessage checks.
		endTurnStream();
		const state = useChatStore.getState();
		const hasStreamingMessage = state.messages.some((m) => m.streaming);
		if (!state.isStreaming && !state.activeTool && !hasStreamingMessage) {
			return false;
		}
		set((s) => ({
			isStreaming: false,
			activeTool: null,
			serverAckPending: false,
			messages: s.messages.map((m) =>
				m.streaming
					? {
							...m,
							streaming: false,
							// Empty placeholder bubbles get a sentinel so the user
							// knows the reply never finished — matches the server
							// Fix 7 sentinel pattern. Bubbles that already have
							// content keep their text (partial reply > nothing).
							content: m.content
								? m.content
								: "⚠ The reply did not complete. Please try again.",
						}
					: m,
			),
		}));
		if (reason && typeof console !== "undefined") {
			// Single info-level log so a developer reading the console can
			// see why the spinner died. Production users never read this.
			console.info(`[chat] forceClearStream: ${reason}`);
		}
		return true;
	},

	loadConversations: async (includeArchived?: boolean) => {
		try {
			const convs = await fetchConversations(includeArchived);
			set({
				conversations: convs.map((c) => ({
					id: c.id,
					title: c.title,
					updatedAt: c.updated_at,
					...(c.archived !== undefined ? { archived: c.archived } : {}),
					...(c.pinned !== undefined ? { pinned: c.pinned } : {}),
					...(c.agent_key ? { agentKey: c.agent_key } : {}),
				})),
				conversationsLoaded: true,
			});
		} catch {
			// Server may not be reachable yet — leave loaded=true so we
			// stop showing skeletons forever on a hard server outage.
			set({ conversationsLoaded: true });
		}
	},

	loadMessages: async (conversationId) => {
		// A buffered delta from a prior turn/thread must not survive a reload.
		resetStreamBuffer();
		try {
			const page = await fetchMessages(conversationId);
			const messages = mapApiMessages(page.items);
			set({
				messages,
				// Oldest loaded row (messages are oldest-first) is the cursor
				// for paging further back on scroll-to-top.
				hasMoreOlder: page.has_more,
				oldestCursor: messages[0]?.createdAt ?? null,
				loadingOlder: false,
			});
		} catch {
			set({ messages: [], hasMoreOlder: false, oldestCursor: null, loadingOlder: false });
		}
	},

	loadOlderMessages: async (conversationId) => {
		const { oldestCursor, hasMoreOlder, loadingOlder, activeConversationId } = get();
		if (!hasMoreOlder || loadingOlder || !oldestCursor) return 0;
		if (activeConversationId !== conversationId) return 0;
		set({ loadingOlder: true });
		try {
			const page = await fetchMessages(conversationId, oldestCursor);
			// A conversation switch mid-fetch invalidates this page.
			if (get().activeConversationId !== conversationId) {
				set({ loadingOlder: false });
				return 0;
			}
			const older = mapApiMessages(page.items, "older");
			// Dedup by id — belt-and-braces against any boundary overlap.
			const seen = new Set(get().messages.map((m) => m.id));
			const fresh = older.filter((m) => !seen.has(m.id));
			if (fresh.length === 0) {
				set({ hasMoreOlder: false, loadingOlder: false });
				return 0;
			}
			set((s) => ({
				messages: [...fresh, ...s.messages],
				oldestCursor: fresh[0]?.createdAt ?? oldestCursor,
				hasMoreOlder: page.has_more,
				loadingOlder: false,
			}));
			return fresh.length;
		} catch {
			set({ loadingOlder: false });
			return 0;
		}
	},

	clearMessages: () => {
		resetStreamBuffer();
		set({ messages: [] });
	},

	setWebSocket: (ws) => {
		wsRef = ws;
	},

	editMessage: async (messageId, newContent) => {
		const state = useChatStore.getState();
		const cid = state.activeConversationId;
		if (!cid || state.isStreaming) return;
		const trimmed = newContent.trim();
		if (!trimmed) return;
		// 1. Persist the edit + truncate the trailing reply server-side.
		const res = await fetch(
			`/api/conversations/${cid}/messages/${messageId}`,
			{
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ content: trimmed }),
			},
		);
		if (!res.ok) {
			throw new Error(`Edit failed (${res.status})`);
		}
		const data = (await res.json()) as {
			replay_text: string;
			branch_group_id?: string | null;
		};
		// 2. Refresh from authoritative server state — the truncation may
		// have removed several rows (assistant + tool traces).
		await state.loadMessages(cid);
		// 3. Add a streaming placeholder so the UI shows the cursor
		// immediately, then ask the WebSocket for a fresh reply. Edit does not
		// branch in Cut 1 (branch_group_id is null), so no reload is scheduled.
		set((s) => ({
			messages: [
				...s.messages,
				{
					id: createMessageId(),
					role: "assistant",
					content: "",
					createdAt: new Date().toISOString(),
					streaming: true,
				},
			],
			isStreaming: true,
			activeTool: null,
			pendingBranchReload: !!data.branch_group_id,
			// The server has not seen this turn yet — don't let the
			// stale-turn reconciler mistake "not started" for "finished".
			serverAckPending: true,
		}));
		wsRef?.sendRegenerate(data.replay_text, data.branch_group_id);
	},

	regenerateLast: async () => {
		const state = useChatStore.getState();
		const cid = state.activeConversationId;
		if (!cid || state.isStreaming) return;
		const res = await fetch(`/api/conversations/${cid}/regenerate`, {
			method: "POST",
		});
		if (!res.ok) {
			throw new Error(`Regenerate failed (${res.status})`);
		}
		const data = (await res.json()) as {
			replay_text: string;
			branch_group_id?: string | null;
		};
		await state.loadMessages(cid);
		// Regenerate DOES branch: the fresh reply becomes a sibling. Schedule a
		// one-shot reload on `done` so the new `< n/m >` count appears (the
		// streamed row carries no branch metadata client-side).
		set((s) => ({
			messages: [
				...s.messages,
				{
					id: createMessageId(),
					role: "assistant",
					content: "",
					createdAt: new Date().toISOString(),
					streaming: true,
				},
			],
			isStreaming: true,
			activeTool: null,
			pendingBranchReload: !!data.branch_group_id,
			// The server has not seen this turn yet — don't let the
			// stale-turn reconciler mistake "not started" for "finished".
			serverAckPending: true,
		}));
		wsRef?.sendRegenerate(data.replay_text, data.branch_group_id);
	},

	switchActiveBranch: async (messageId, index) => {
		const state = useChatStore.getState();
		const cid = state.activeConversationId;
		if (!cid || state.isStreaming) return;
		const { switchBranch } = await import("../lib/api");
		await switchBranch(cid, messageId, index); // toast on failure via apiCall
		// Reload to reflect the switched path (the target reply + its tools).
		await state.loadMessages(cid);
	},

	clearPendingBranchReload: () => set({ pendingBranchReload: false }),
}));
